/* Harper & Stone — header behaviour.
 *
 * Three web components, no dependencies. Everything degrades: the nav is real
 * links, the drawer is a <dialog>, search is a <form> that submits.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

/* The header is pinned by CSS alone — `position: sticky`, no scroll handler.
 *
 * This used to listen to every scroll event to retract the bar on scroll-down
 * and restore it on scroll-up. The header no longer hides at all, so the
 * listener, the rAF throttle and the open-panel guard all went with it: a
 * scroll handler that exists only to set a state nothing reads is a
 * frame-rate cost for nothing.
 *
 * What remains is one measurement. The announcement scrolls out above the
 * pinned header because the sticky group is offset upward by the bar's
 * height, and only layout knows that height: the copy wraps to two lines on
 * a narrow screen, and a dismissed bar is zero. A ResizeObserver publishes it
 * to the sticky wrapper — the section div Shopify renders around this
 * element — since a custom property set on this element would never reach
 * the rule on its parent. */
class HsHeaderGroup extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const bar = this.querySelector('.announcement');
    const row = this.querySelector('.header');
    if (!bar && !row) return;

    const target = this.closest('.shopify-section') ?? this;

    this.#observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = Math.round(
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height,
        );
        if (entry.target === bar) {
          target.style.setProperty('--announcement-height', `${h}px`);
        } else {
          // Published on :root because anything that sticks below the header
          // needs it — the collection toolbar and the product page's info
          // column both read it, and neither is inside this element. The old
          // theme set the same property from its own header.js, which this
          // theme does not load.
          document.documentElement.style.setProperty('--header-height', `${h}px`);
        }
      }
    });

    if (bar) this.#observer.observe(bar);
    if (row) this.#observer.observe(row);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

/* Desktop mega menu. Opens on hover with intent, on click, and on keyboard. */
class HsNav extends HTMLElement {
  #openTimer = null;
  #closeTimer = null;
  #trail = [];

  connectedCallback() {
    this.scrim = document.querySelector('[data-header-scrim]');
    this.items = [...this.querySelectorAll('[data-nav-item]')];

    for (const item of this.items) {
      const trigger = item.querySelector('[data-nav-trigger]');
      const panel = item.querySelector('[data-nav-panel]');
      if (!trigger || !panel) continue;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.#toggle(item, trigger.getAttribute('aria-expanded') !== 'true');
      });

      /* Hover, with aim.
       *
       * A panel's links sit at its far left, so from "About" or "Shop by
       * Shape" the pointer travels a long shallow diagonal to reach them —
       * straight across the neighbouring triggers. Each one it crossed
       * opened its own panel, so the menu the shopper was heading for was
       * swapped out from under them on the way. "Jewelry" never showed it:
       * its links are directly below its trigger. Reproduced with a 900ms
       * diagonal from About; the panel was replaced at 660ms.
       *
       * The fix is the usual menu-aim rule. The panel spans the full width
       * beneath the row, so *any* downward travel is travel toward it. While
       * a panel is open and the pointer is heading down, a trigger it passes
       * over has to be dwelt on before it takes over; a pointer moving along
       * the row, level, still switches at once. */
      item.addEventListener('pointerenter', (e) => {
        if (matchMedia('(hover: none)').matches) return;
        clearTimeout(this.#closeTimer);
        clearTimeout(this.#openTimer);
        if (trigger.getAttribute('aria-expanded') === 'true') return;
        if (!(this.#anyOpen() && this.#aimedAtPanel(e))) {
          this.#openTimer = setTimeout(() => this.#toggle(item, true), 65);
          return;
        }

        /* Aimed at the open panel: this trigger is probably just in the
         * way. It takes over only once the pointer stops aiming — comes to
         * rest here, or levels out along the row. A fixed dwell was tried
         * (340ms) and a slow hand simply outlasted it; asking again costs
         * nothing and has no speed at which it fails. `pointerleave` clears
         * the timer, so a pointer that passes through never opens it. */
        const ask = () => {
          const last = this.#trail[this.#trail.length - 1];
          const still = last && this.#aimedAtPanel({ clientX: last.x, clientY: last.y });
          if (still) this.#openTimer = setTimeout(ask, 120);
          else this.#toggle(item, true);
        };
        this.#openTimer = setTimeout(ask, 200);
      });

      /* Leaving a trigger into the rest of the header — the 13px strip
       * between the row and the panel, or the space between two triggers —
       * is not leaving the menu, and gets a longer grace than leaving the
       * header altogether. At 220ms a slow diagonal closed the panel while
       * the pointer was still on its way to it, and the panel then reopened
       * when the pointer arrived: open, close, open. */
      item.addEventListener('pointerleave', (e) => {
        if (matchMedia('(hover: none)').matches) return;
        clearTimeout(this.#openTimer);
        const stillInHeader = this.header?.contains(e.relatedTarget);
        this.#closeTimer = setTimeout(() => this.#closeAll(), stillInHeader ? 480 : 220);
      });
    }

    this.header = this.closest('.header');
    this.onMove = (e) => {
      const now = performance.now();
      this.#trail.push({ x: e.clientX, y: e.clientY, t: now });
      while (this.#trail.length > 1 && now - this.#trail[0].t > 160) this.#trail.shift();
    };
    this.addEventListener('pointermove', this.onMove, { passive: true });

    this.onKeydown = (e) => {
      if (e.key === 'Escape') this.#closeAll(true);
    };
    document.addEventListener('keydown', this.onKeydown);
    this.scrim?.addEventListener('click', () => this.#closeAll());

    // Focus leaving the nav entirely closes it — keyboard users should not
    // have to press Escape to tidy up after themselves.
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.#closeAll();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }

  #anyOpen() {
    return this.items.some(
      (i) => i.querySelector('[data-nav-trigger]')?.getAttribute('aria-expanded') === 'true',
    );
  }

  /* Is the pointer on its way to the open panel?
   *
   * Extend the line from where the pointer was ~160ms ago through where it
   * is now, down to the panel's top edge. If it lands on the panel, the
   * pointer is aimed at it. "Moving downward" alone was tried and is not
   * enough: from About to the links at the far left the path drops 2px for
   * every 19 it travels sideways, which no downward threshold separates
   * from a level hand. The projection does — that shallow diagonal lands
   * well inside the panel, while a level move with a pixel of jitter
   * projects thousands of pixels off screen. */
  #aimedAtPanel(e) {
    const now = performance.now();
    const from = this.#trail.find((s) => now - s.t <= 160);
    if (!from || !this.header) return false;

    const dy = e.clientY - from.y;
    if (dy < 1.5) return false;

    const panelTop = this.header.getBoundingClientRect().bottom;
    const steps = (panelTop - from.y) / dy;
    const landsAt = from.x + (e.clientX - from.x) * steps;
    return landsAt >= 0 && landsAt <= window.innerWidth;
  }

  #toggle(item, open) {
    for (const other of this.items) {
      if (other === item) continue;
      this.#set(other, false);
    }
    this.#set(item, open);
    if (this.scrim) this.scrim.dataset.open = String(open);
  }

  #set(item, open) {
    const trigger = item.querySelector('[data-nav-trigger]');
    const panel = item.querySelector('[data-nav-panel]');
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', String(open));
    panel.dataset.open = String(open);
  }

  #closeAll(returnFocus = false) {
    const openItem = this.items.find(
      (i) => i.querySelector('[data-nav-trigger]')?.getAttribute('aria-expanded') === 'true'
    );
    for (const item of this.items) this.#set(item, false);
    if (this.scrim) this.scrim.dataset.open = 'false';
    if (returnFocus) openItem?.querySelector('[data-nav-trigger]')?.focus();
  }
}

/* Mobile drawer. Native <dialog> gives focus trapping and Escape for free;
 * this only adds the second level, which the reference never captured. */
class HsDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.opener = document.querySelector('[data-drawer-open]');
    if (!this.dialog) return;

    this.opener?.addEventListener('click', () => this.open());

    for (const close of this.querySelectorAll('[data-drawer-close]')) {
      close.addEventListener('click', () => this.close());
    }

    // Anything inside the drawer that opens another surface closes this one
    // first: two stacked modals trap focus in the wrong place.
    for (const el of this.querySelectorAll('[data-search-open]')) {
      el.addEventListener('click', () => this.close());
    }

    for (const trigger of this.querySelectorAll('[data-panel-open]')) {
      trigger.addEventListener('click', () => {
        const panel = this.querySelector(`#${trigger.dataset.panelOpen}`);
        if (!panel) return;
        panel.dataset.open = 'true';
        panel.querySelector('[data-panel-back]')?.focus();
      });
    }

    for (const back of this.querySelectorAll('[data-panel-back]')) {
      back.addEventListener('click', () => {
        const panel = back.closest('[data-panel]');
        if (!panel) return;
        panel.dataset.open = 'false';
        this.querySelector(`[data-panel-open="${panel.id}"]`)?.focus();
      });
    }

    // Escape closes the open sub-panel first, the drawer second.
    this.dialog.addEventListener('cancel', (e) => {
      const open = this.querySelector('[data-panel][data-open="true"]');
      if (open) {
        e.preventDefault();
        open.dataset.open = 'false';
      }
    });

    this.dialog.addEventListener('close', () => {
      document.body.style.removeProperty('overflow');
      this.opener?.focus();
    });

    // Theme editor: a Menu column block lives in a closed panel of a closed
    // dialog, so selecting it in the sidebar showed nothing. Open the drawer
    // and that panel while it is selected, close again when it is not.
    this.editor?.abort();
    this.editor = new AbortController();
    const { signal } = this.editor;
    document.addEventListener(
      'shopify:block:select',
      (e) => {
        if (!this.contains(e.target)) return;
        if (!this.dialog.open) this.open();
        const panel = e.target.closest('[data-panel]');
        if (panel) panel.dataset.open = 'true';
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:deselect',
      (e) => {
        if (this.contains(e.target) && this.dialog.open) this.close();
      },
      { signal }
    );

    // A click on the backdrop closes. <dialog> reports those as clicks on
    // itself, so compare against the dialog's own box.
    this.dialog.addEventListener('click', (e) => {
      if (e.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) this.close();
    });
  }

  disconnectedCallback() {
    this.editor?.abort();
  }

  open() {
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  close() {
    for (const panel of this.querySelectorAll('[data-panel]')) panel.dataset.open = 'false';
    this.dialog.close();
  }
}

if (!customElements.get('hs-header-group')) customElements.define('hs-header-group', HsHeaderGroup);
if (!customElements.get('hs-nav')) customElements.define('hs-nav', HsNav);
if (!customElements.get('hs-drawer')) customElements.define('hs-drawer', HsDrawer);

/* Announcement rotator. Pauses on hover and focus, and does not run at all
 * under prefers-reduced-motion — a rotating banner is exactly the kind of
 * motion that setting exists for. */
class HsAnnouncement extends HTMLElement {
  #index = 0;
  #timer = null;

  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-announcement-item]')];

    // Dismissal sticks for the session, not forever: a shipping message the
    // visitor closed on Monday should still be there next week.
    this.close = this.querySelector('[data-announcement-close]');
    try {
      if (sessionStorage.getItem('hs:announcement-dismissed') === '1') {
        this.hidden = true;
        return;
      }
    } catch (e) {
      // Private browsing can throw on storage access; the bar simply stays.
    }

    this.close?.addEventListener('click', () => {
      this.hidden = true;
      try {
        sessionStorage.setItem('hs:announcement-dismissed', '1');
      } catch (e) {
        /* nothing to do */
      }
    });

    if (this.items.length < 2 || REDUCED.matches) return;

    this.addEventListener('pointerenter', () => this.#stop());
    this.addEventListener('pointerleave', () => this.#start());
    this.addEventListener('focusin', () => this.#stop());
    this.addEventListener('focusout', () => this.#start());
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #start() {
    this.#stop();
    this.#timer = setInterval(() => this.#next(), 6000);
  }

  #stop() {
    clearInterval(this.#timer);
  }

  #next() {
    this.items[this.#index].setAttribute('aria-hidden', 'true');
    this.#index = (this.#index + 1) % this.items.length;
    this.items[this.#index].setAttribute('aria-hidden', 'false');
  }
}

if (!customElements.get('hs-announcement')) customElements.define('hs-announcement', HsAnnouncement);
